/**
 * Сохранение решения сметчика по находке (разметка HITL) с видимой ошибкой.
 *
 * Таблицы разметки вызывали мутацию как `annotate(...)` или
 * `annotate(...).catch(...)`. Промис мутации RTK Query при ошибке сервера не
 * отклоняется — он разрешается объектом `{ error }`, поэтому `catch` не
 * срабатывал, а результат никто не читал: решение не сохранялось, и сметчик
 * об этом не узнавал (замечание F05 экспертизы 03.11.2026). Ошибку даёт только
 * `.unwrap()` — он и вызывается здесь, а текст ошибки показывает таблица.
 */
import { useCallback, useState } from 'react';
import { apiErrorText } from '@shared/api/errorText';

const FAILED = 'Не удалось сохранить решение';

/** Запуск мутации RTK Query: у результата есть `unwrap()`. */
export type Unwrappable = () => { unwrap(): Promise<unknown> };

function finish(text: string): string {
  const t = text.trim();
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

/** Первую букву — в строчную, только если за ней строчная («Нет связи»), а не
 *  аббревиатура или шифр («ЛСР», «Ф3»). */
function lowerFirst(text: string): string {
  const [first, second] = text;
  const secondIsLower = !!second && second !== second.toUpperCase();
  return first && secondIsLower ? first.toLowerCase() + text.slice(1) : text;
}

/**
 * Текст для сметчика: что не получилось и почему. Нет связи или сбой сервера —
 * с подсказкой повторить; отказ по правам или устаревшая находка — без неё
 * (повтор тут не поможет).
 */
export function decisionSaveErrorText(err: unknown): string {
  const text = apiErrorText(err, FAILED);
  if (text.startsWith(FAILED)) return finish(text);
  return finish(`${FAILED}: ${lowerFirst(text)}`);
}

/** Выполнить сохранение; `null` — сохранено, иначе текст ошибки. */
export async function saveDecision(run: Unwrappable): Promise<string | null> {
  try {
    await run().unwrap();
    return null;
  } catch (err) {
    return decisionSaveErrorText(err);
  }
}

/** Состояние сохранения решения для таблицы разметки. */
export function useDecisionSave() {
  const [error, setError] = useState<string | null>(null);
  const save = useCallback(async (run: Unwrappable) => {
    setError(null);
    const message = await saveDecision(run);
    if (message) setError(message);
    return message === null;
  }, []);
  return { error, save };
}
